import {
    type ComponentPropsWithoutRef,
    type ReactNode,
    useRef,
    useCallback,
    useLayoutEffect,
} from "react"
import styles from "./Slider.module.css"

export type SliderType = "default" | "variant2" | "variant3" | "variant4"

interface SliderProps extends ComponentPropsWithoutRef<"input"> {
    min: number
    max: number
    children?: ReactNode
    type?: SliderType
    useWhiteBackground?: boolean
}

const Slider = ({
    type = "default",
    children,
    className,
    useWhiteBackground = false,
    ...props
}: SliderProps) => {
    const sliderRef = useRef<HTMLInputElement>(null)

    const fillUpperRange = useCallback(
        (target: HTMLInputElement) => {
            const max = parseFloat(target.max)
            const min = parseFloat(target.min)
            const value = parseFloat(target.value)
            const percentOfMax = ((value - min) / (max - min)) * 100
            const rightColor = useWhiteBackground
                ? "var(--main-opposite, #202020)"
                : "var(--grey-10, #ffffff)"

            target.style.background = `linear-gradient(90deg, var(--grey-15,rgb(129, 127, 127)) 0%, var(--grey-15,rgb(190, 187, 187)) ${percentOfMax}%, ${rightColor} ${percentOfMax}%, ${rightColor} 100%)`
        },
        [useWhiteBackground]
    )

    const handleInputFillUpperRange = useCallback(
        (e: Event) => {
            fillUpperRange(e.currentTarget as HTMLInputElement)
        },
        [fillUpperRange]
    )

    useLayoutEffect(() => {
        const effectSliderRef = sliderRef.current

        if (type == "default" && sliderRef.current) {
            sliderRef.current.addEventListener(
                "input",
                handleInputFillUpperRange
            )
            fillUpperRange(sliderRef.current)
        }

        return () => {
            effectSliderRef?.removeEventListener(
                "input",
                handleInputFillUpperRange
            )
        }
    }, [type, handleInputFillUpperRange, fillUpperRange, props.value])

    return (
        <div className={`${styles.slider} ${className} ${styles[type]}`}>
            {children}
            <input ref={sliderRef} type="range" {...props} />
        </div>
    )
}

export default Slider
